import Checkbox from '@deliveryhero/pd-cookbook/components/Checkbox';
import { Typography } from '@deliveryhero/pd-cookbook/components/Typography';
import { Grid, MenuItem } from '@mui/material';
import { FormikHelpers } from 'formik';
import { PhoneMask } from 'src/components/PhoneMask';

import FormContainer from 'components/Forms/FormContainer';
import { TextField } from 'components/TextField';

import { LabelText, TestId } from './constants';
import { EmployeeDetailFormProps, EmployeeDetailFormValues } from './types';
import { getEmployeeDetailSchema } from './validation';

const variant = 'outlined';

export const EmployeeDetailForm = (props: EmployeeDetailFormProps) => {
  const {
    onSubmit,
    buttons,
    initialValues,
    costCenters,
    departments,
    expenseCodes,
    detailFormRef,
    countryCode,
    isPhoneRequired = true,
    editable = true,
    isNewUser = false,
    isForRightDrawer = false
  } = props;

  let drawerStyles = {};
  if (isForRightDrawer) {
    drawerStyles = { maxWidth: '100%', flexBasis: '100%' };
  }

  const handleSubmit = (
    values: EmployeeDetailFormValues,
    actions: FormikHelpers<EmployeeDetailFormValues>
  ) => {
    const mobileCountryCode = values.mobile_number.trim().split(' '); // should be ["+65", ...]

    if (isPhoneRequired && mobileCountryCode.length === 1) {
      actions.setFieldError('mobile_number', 'Invalid country code');
      return;
    }

    const data = {
      ...values,
      mobile_country_code: mobileCountryCode[0],
      mobile_number: values.mobile_number.substring(
        values.mobile_number.indexOf(' ') + 1
      )
    };

    onSubmit(data, actions);
  };

  return (
    <FormContainer
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={getEmployeeDetailSchema(isPhoneRequired)}
      buttons={buttons}
      formRef={detailFormRef}
    >
      {({ handleChange, values, touched, errors, setFieldValue }) => (
        <Grid container spacing={2}>
          {isForRightDrawer && (
            <Grid item xs={12}>
              <Typography type="title-sm" color="neutral-secondary">
                {LabelText.DRAWER_TITLE_BASIC_INFORMATION}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} sm={6} style={drawerStyles}>
            <TextField
              id="first_name"
              name="first_name"
              label={LabelText.FIRST_NAME}
              value={values.first_name}
              onChange={handleChange}
              variant={variant}
              fullWidth
              error={touched.first_name && errors.first_name ? true : false}
              helperText={touched.first_name && errors.first_name}
              disabled={!editable}
              sx={{
                label: {
                  background: 'white'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} style={drawerStyles}>
            <TextField
              id="last_name"
              name="last_name"
              label={LabelText.LAST_NAME}
              value={values.last_name}
              onChange={handleChange}
              variant={variant}
              fullWidth
              error={touched.last_name && errors.last_name ? true : false}
              helperText={touched.last_name && errors.last_name}
              disabled={!editable}
              sx={{
                label: {
                  background: 'white'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} style={drawerStyles}>
            <TextField
              id="email"
              name="email"
              label={LabelText.EMAIL}
              value={values.email}
              onChange={handleChange}
              variant={variant}
              fullWidth
              error={touched.email && errors.email ? true : false}
              helperText={touched.email && errors.email}
              disabled={!(editable && isNewUser)}
              sx={{
                label: {
                  background: 'white'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} style={drawerStyles}>
            <TextField
              id="mobile_number"
              name="mobile_number"
              label={
                isPhoneRequired
                  ? LabelText.PHONE_REQUIRED
                  : LabelText.PHONE_OPTIONAL
              }
              value={values.mobile_number}
              onChange={handleChange}
              InputProps={{
                inputComponent: PhoneMask as any,
                inputProps: {
                  countryCode: countryCode.toLowerCase(),
                  'data-testid': TestId.phone
                }
              }}
              variant={variant}
              error={
                touched.mobile_number && errors.mobile_number ? true : false
              }
              helperText={touched.mobile_number && errors.mobile_number}
              disabled={!editable}
              sx={{
                label: {
                  background: 'white'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} style={drawerStyles}>
            <TextField
              select
              id="department_id"
              name="department_id"
              label={LabelText.DEPARTMENT}
              value={values.department_id}
              onChange={handleChange}
              disabled={!editable}
              error={
                touched.department_id && errors.department_id ? true : false
              }
              helperText={touched.department_id && errors.department_id}
              sx={{
                label: {
                  background: 'white'
                }
              }}
            >
              {departments.length === 0 && <MenuItem value={0}>None</MenuItem>}
              {departments.map((department) => (
                <MenuItem key={department.id} value={department.id}>
                  {department.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} style={drawerStyles}>
            <TextField
              id="employee_code"
              name="employee_code"
              label={LabelText.EMPLOYEE_ID}
              value={values.employee_code}
              onChange={handleChange}
              variant={variant}
              fullWidth
              helperText={touched.employee_code && errors.employee_code}
              disabled={!editable}
              sx={{
                label: {
                  background: 'white'
                }
              }}
            />
          </Grid>
          {isForRightDrawer && (
            <Grid item xs={12}>
              <Typography type="title-sm" color="neutral-secondary">
                {LabelText.DRAWER_TITLE_EXPENSE}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} sm={6} style={drawerStyles}>
            <TextField
              select
              name="expense_codes"
              id="expense_codes"
              label={LabelText.EXPENSE_CODES}
              value={values.expense_codes}
              onChange={handleChange}
              disabled={!editable}
              inputProps={{
                'data-testid': 'expense-codes-select'
              }}
              SelectProps={{
                multiple: true
              }}
              sx={{
                label: {
                  background: 'white'
                }
              }}
            >
              {expenseCodes.length === 0 && <MenuItem disabled>None</MenuItem>}
              {expenseCodes.map((center) => (
                <MenuItem key={center.id} value={center.id}>
                  {center.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} style={drawerStyles}>
            <TextField
              select
              name="cost_center_id"
              id="cost_center_id"
              label={LabelText.COST_CENTER}
              value={values.cost_center_id}
              onChange={handleChange}
              disabled={!editable}
              sx={{
                label: {
                  background: 'white'
                }
              }}
            >
              <MenuItem value={0}>None</MenuItem>
              {costCenters.map((center) => (
                <MenuItem key={center.id} value={center.id}>
                  {center.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} style={drawerStyles}>
            <Checkbox
              name="is_eligible_for_all_expense_codes"
              id="is_eligible_for_all_expense_codes"
              label={LabelText.CHECKBOX_LABEL_EXPENSE}
              checked={values.is_eligible_for_all_expense_codes}
              onChange={(e) => {
                setFieldValue(
                  'is_eligible_for_all_expense_codes',
                  e.target.checked
                );
              }}
            />
          </Grid>
        </Grid>
      )}
    </FormContainer>
  );
};
